import { useState, useEffect, useCallback } from "react";

// ════════════════════════════════════════════════════════════════
// SKU Mapping — link Shopify SKUs to marketplace SKUs
// So inventory + P&L can be unified across platforms
// ════════════════════════════════════════════════════════════════

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c" },
  { id:"amazon",   name:"Amazon",   color:"#c45000" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089" },
];

export default function PageSkuMapping({ toast, products }) {
  const [mappings, setMappings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_sku_map")||"{}"); } catch { return {}; }
  });
  const [search, setSearch] = useState("");
  const [newMaster, setNewMaster] = useState("");

  const save = useCallback((m) => {
    setMappings(m);
    localStorage.setItem("3june_sku_map", JSON.stringify(m));
  }, []);

  // Auto-detect SKUs from all sources
  const detectAllSkus = useCallback(() => {
    const found = {};
    // Shopify products (passed as prop)
    (products||[]).forEach(p => {
      const sku = p.shopifySku || p.sku;
      if (sku) found[sku] = { shopify: sku };
    });
    // Myntra orders from localStorage
    try {
      const myntra = JSON.parse(localStorage.getItem("3june_myntra_orders")||"[]");
      myntra.forEach(o => { if (o.sku && !found[o.sku]) found[o.sku] = { myntra: o.sku }; });
    } catch {}
    // Marketplace orders
    try {
      const mp = JSON.parse(localStorage.getItem("3june_mp_orders")||"[]");
      mp.forEach(o => { if (o.sku && !found[o.sku]) found[o.sku] = { [o.platform]: o.sku }; });
    } catch {}

    // Merge into existing mappings (don't overwrite)
    const updated = { ...mappings };
    Object.entries(found).forEach(([sku, plat]) => {
      if (!updated[sku]) {
        updated[sku] = { master: sku, ...plat };
      }
    });
    save(updated);
    toast(`${Object.keys(found).length} SKUs detected`, "success");
  }, [products, mappings, save, toast]);

  const addMaster = () => {
    if (!newMaster.trim()) return;
    if (mappings[newMaster]) { toast("That master SKU already exists", "error"); return; }
    save({ ...mappings, [newMaster]: { master: newMaster } });
    setNewMaster("");
  };

  const updateMapping = (master, platform, value) => {
    save({ ...mappings, [master]: { ...mappings[master], [platform]: value } });
  };

  const deleteMapping = (master) => {
    const m = { ...mappings };
    delete m[master];
    save(m);
  };

  const exportCSV = () => {
    const hdr = ["Master SKU", ...PLATFORMS.map(p=>p.name)];
    const rows = Object.entries(mappings).map(([master,v]) =>
      [master, ...PLATFORMS.map(p=>v[p.id]||"")]
    );
    const csv = [hdr,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv,"+encodeURIComponent(csv);
    a.download = "sku_mapping.csv";
    a.click();
    toast("Exported ✓", "success");
  };

  const entries = Object.entries(mappings)
    .filter(([master,v]) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return master.toLowerCase().includes(q) ||
        PLATFORMS.some(p => (v[p.id]||"").toLowerCase().includes(q));
    })
    .sort((a,b) => a[0].localeCompare(b[0]));

  // Count how many are fully mapped (2+ platforms)
  const fullyMapped = Object.values(mappings).filter(v =>
    PLATFORMS.filter(p => v[p.id]).length >= 2
  ).length;

  const S = {
    card: { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:16 },
    btn:  (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, background:c, color:"#fff", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:600, fontSize:13 }),
    btnOut: { padding:"8px 14px", borderRadius:7, background:"transparent", border:"1px solid #e5e7eb", color:"#6b7280", cursor:"pointer", fontFamily:"inherit", fontSize:12 },
    input: { padding:"7px 10px", border:"1px solid #d1d5db", borderRadius:6, fontSize:12, fontFamily:"inherit", width:"100%" },
  };

  return (
    <div style={{ padding:"24px 28px", maxWidth:1400, margin:"0 auto" }}>
      <div style={{ marginBottom:18 }}>
        <div style={{ fontWeight:700, fontSize:20, fontFamily:"Georgia,serif" }}>🔗 SKU Mapping</div>
        <div style={{ fontSize:13, color:"#9ca3af", marginTop:2 }}>Link the same product's SKU across platforms — so inventory and P&L stay unified</div>
      </div>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:18 }}>
        {[
          { label:"Total SKUs", value:Object.keys(mappings).length, color:"#1A3A5C" },
          { label:"Mapped (2+ platforms)", value:fullyMapped, color:"#15803d" },
          { label:"Unmapped", value:Object.keys(mappings).length-fullyMapped, color:"#d97706" },
        ].map(k=>(
          <div key={k.label} style={{ background:"#fff", borderRadius:9, border:"1px solid #e5e7eb", padding:"12px 16px", borderLeft:"3px solid "+k.color }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        <input placeholder="🔍 Search SKU…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ ...S.input, width:220 }}/>
        <input placeholder="New master SKU…" value={newMaster} onChange={e=>setNewMaster(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&addMaster()}
          style={{ ...S.input, width:180 }}/>
        <button style={S.btn()} onClick={addMaster}>+ Add</button>
        <button style={S.btnOut} onClick={detectAllSkus}>🔄 Auto-detect from data</button>
        <div style={{ flex:1 }}/>
        <button style={S.btn("#15803d")} onClick={exportCSV}>⬇ Export CSV</button>
      </div>

      {/* Table */}
      <div style={S.card}>
        {entries.length === 0 ? (
          <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>
            <div style={{ fontSize:36, marginBottom:10 }}>🔗</div>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No SKU mappings yet</div>
            <div style={{ fontSize:13, marginBottom:16 }}>Click "Auto-detect from data" to pull SKUs from Shopify, Myntra and marketplace uploads.</div>
            <button style={S.btn()} onClick={detectAllSkus}>🔄 Auto-detect now</button>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#f9fafb" }}>
                  <th style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", color:"#9ca3af" }}>Master SKU</th>
                  {PLATFORMS.map(p => (
                    <th key={p.id} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, letterSpacing:"0.5px", textTransform:"uppercase", color:p.color }}>{p.name}</th>
                  ))}
                  <th style={{ padding:"10px 12px", width:40 }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([master,v],i) => (
                  <tr key={master} style={{ background:i%2?"#fafafa":"#fff", borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"8px 12px", fontFamily:"monospace", fontSize:12, fontWeight:600, color:"#1A3A5C" }}>{master}</td>
                    {PLATFORMS.map(p => (
                      <td key={p.id} style={{ padding:"6px 12px" }}>
                        <input value={v[p.id]||""} placeholder="—"
                          onChange={e=>updateMapping(master, p.id, e.target.value)}
                          style={{ ...S.input, fontFamily:"monospace", fontSize:11, border: v[p.id]?`1px solid ${p.color}44`:"1px solid #f0f0f0", background: v[p.id]?p.color+"08":"#fff" }}/>
                      </td>
                    ))}
                    <td style={{ padding:"6px 12px", textAlign:"center" }}>
                      <span onClick={()=>deleteMapping(master)} style={{ cursor:"pointer", color:"#9ca3af", fontSize:16 }} title="Delete">×</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background:"#eff6ff", borderRadius:8, padding:"12px 16px", fontSize:12, color:"#1e40af", lineHeight:1.6 }}>
        💡 <strong>Why map SKUs?</strong> If you sell the same belt as <code>AT_03_Black_36</code> on Myntra but <code>BELT-AT03-BLK-36</code> on Amazon, mapping them to one master SKU lets 3June show combined inventory, total units sold across platforms, and which platform gives the best margin for that product.
      </div>
    </div>
  );
}
